import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { type AxiosError, isAxiosError } from 'axios';
import type { TPathwayConfigFromUser } from '@timeedit/types/lib/types/pathwayConfigFromUser.type';
import type { TMappings } from '../pages/appSettings/dataManagerSettings/studyCombinationsMapping';
import api from '../services/api.service';
import { configService } from '../services/config.service';
import type { TRootState } from './store';
import { createAppAsyncThunk } from './utils';

type TStudyCombinationsState = {
  isLoadingPathwayConfigs: boolean;
  isSavingPathwayConfigs: boolean;
  fetchingPathwayConfigsError: string | null;
  pathwayConfigs: TPathwayConfigFromUser[];
  mappingChanges: TMappings;
};

const initialState: TStudyCombinationsState = {
  isLoadingPathwayConfigs: false,
  isSavingPathwayConfigs: false,
  fetchingPathwayConfigsError: null,
  pathwayConfigs: [],
  mappingChanges: {},
};

const slice = createSlice({
  name: 'studyCombinations',
  initialState,
  reducers: {
    fetchPathwayConfigsRequest: (state) => {
      state.isLoadingPathwayConfigs = true;
    },
    fetchPathwayConfigsSuccess: (
      state,
      action: PayloadAction<{
        data: { pathwayConfigFromUser: TPathwayConfigFromUser[] };
      }>
    ) => {
      state.pathwayConfigs = action.payload.data.pathwayConfigFromUser;
      state.fetchingPathwayConfigsError = null;
      state.isLoadingPathwayConfigs = false;
    },
    fetchPathwayConfigsFailure: (
      state,
      action: PayloadAction<Error | AxiosError>
    ) => {
      const errorMessage = action.payload.message;
      const errorDetails = isAxiosError(action.payload)
        ? action.payload.response?.data?.message
        : null;

      state.fetchingPathwayConfigsError = `${errorMessage ?? ''}${
        errorMessage && errorDetails ? ' - ' : ''
      }${errorDetails ?? ''}`;

      state.isLoadingPathwayConfigs = false;
    },
    updatePathwayConfigsRequest: (state) => {
      state.isSavingPathwayConfigs = true;
    },
    updatePathwayConfigsSuccess: (
      state,
      action: PayloadAction<{ data: TPathwayConfigFromUser[] }>
    ) => {
      const updatedPathwayConfigs = action.payload
        .data as TPathwayConfigFromUser[];
      state.pathwayConfigs = state.pathwayConfigs.map((pathwayConfig) => {
        const updatedPathwayConfig = updatedPathwayConfigs.find(
          (updatedPathwayConfig) =>
            updatedPathwayConfig._id.toString() === pathwayConfig._id.toString()
        );
        return updatedPathwayConfig ?? pathwayConfig;
      });
      state.mappingChanges = {};
      state.isSavingPathwayConfigs = false;
    },
    updatePathwayConfigsFailure: (state) => {
      state.isSavingPathwayConfigs = false;
    },
    setMappingChangesSuccess: (state, action: PayloadAction<TMappings>) => {
      state.mappingChanges = action.payload;
    },
  },
});

export default slice.reducer;

// Selectors
export const pathwayConfigsSelector: (
  state: TRootState
) => TPathwayConfigFromUser[] = (state: TRootState) =>
  state.studyCombinations.pathwayConfigs;

export const isLoadingPathwayConfigsSelector: (state: TRootState) => boolean = (
  state: TRootState
) => state.studyCombinations.isLoadingPathwayConfigs;

export const fetchingPathwayConfigsErrorSelector: (
  state: TRootState
) => string = (state: TRootState) =>
  state.studyCombinations.fetchingPathwayConfigsError;

export const isSavingPathwayConfigsSelector: (state: TRootState) => boolean = (
  state: TRootState
) => state.studyCombinations.isSavingPathwayConfigs;

export const mappingChangesSelector: (state: TRootState) => TMappings = (
  state: TRootState
) => state.studyCombinations.mappingChanges;

// Actions
export const {
  fetchPathwayConfigsRequest,
  fetchPathwayConfigsSuccess,
  fetchPathwayConfigsFailure,
  updatePathwayConfigsRequest,
  updatePathwayConfigsSuccess,
  updatePathwayConfigsFailure,
  setMappingChangesSuccess,
} = slice.actions;

export const fetchPathwayConfigs = createAppAsyncThunk(
  'studyCombinations/fetchPathwayConfigs',
  async (payload, { dispatch }) => {
    try {
      const endpoint = `${configService.REACT_APP_PATHWAY_SERVICE_URL}/pathway-config-from-user`;
      dispatch(fetchPathwayConfigsRequest());
      const response = await api.get<
        void,
        {
          data: {
            pathwayConfigFromUser: TPathwayConfigFromUser[];
          };
        }
      >({
        endpoint,
      });
      dispatch(fetchPathwayConfigsSuccess(response));
    } catch (error) {
      dispatch(fetchPathwayConfigsFailure(error));
    }
  }
);

export const updatePathwayConfigs = createAppAsyncThunk(
  'studyCombinations/updatePathwayConfigs',
  async (
    payload: Pick<TPathwayConfigFromUser, '_id'> &
      Partial<TPathwayConfigFromUser>[],
    { dispatch }
  ) => {
    try {
      const endpoint = `${configService.REACT_APP_PATHWAY_SERVICE_URL}/pathway-config-from-user`;
      dispatch(updatePathwayConfigsRequest());
      const response = await api.patch<
        { pathwayConfigsFromUser: typeof payload },
        { data: TPathwayConfigFromUser[] }
      >({
        endpoint,
        data: {
          pathwayConfigsFromUser: payload,
        },
      });
      dispatch(updatePathwayConfigsSuccess(response));
    } catch (error) {
      dispatch(updatePathwayConfigsFailure());
    }
  }
);

export const setMappingChanges = createAppAsyncThunk(
  'studyCombinations/setMappingChanges',
  async (payload: TMappings, { dispatch }) => {
    dispatch(setMappingChangesSuccess(payload));
  }
);
